import { env } from "environment-interface/env";
import { useBranch } from "features/branch";
import { useBranchEditor } from "features/editor/Feature";
import { useSession } from "hooks/api/useSession";
import { hasBranchWriteAccess } from "isomorphic/permission";

import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useBranchingAnimation } from "hooks/useBranchingAnimation";
import { useEditorRouter } from "hooks/useEditorRouter";
import { useGitHubScopes } from "hooks/useGitHubScopes";
import { paths } from "utils/router";

import type { Command } from "../../types";

type Target = "DEFAULT" | "CURRENT" | "CONTRIBUTION_BRANCH";

const getName = (target: Target, defaultBranchName: string) => {
  if (target === "CONTRIBUTION_BRANCH") return "Create Contribution Branch";
  if (target === "DEFAULT") return `Create branch from '${defaultBranchName}'`;

  return "Create branch from current";
};

export const useForkBranchCommand = ({
  target,
  newBranchName,
}: {
  target: Target;
  newBranchName?: string;
}): Command => {
  const editor = useBranchEditor();
  const {
    defaultBranchName,
    owner,
    repo,
    branch: name,
    workspace,
    authorization,
    isPrivate,
  } = editor;

  const gitHubScopes = useGitHubScopes();
  const userWorkspace = useActiveWorkspace();
  const { startBranchingAnimation } = useBranchingAnimation();
  const [, sessionApi] = useSession();
  const router = useEditorRouter();
  const [, dispatchBranch] = useBranch();

  const sourceBranchName = target === "DEFAULT" ? defaultBranchName : name;

  return {
    id: "codesandbox.branch.fork",
    name: getName(target, defaultBranchName),
    section: "Git",
    perform: async () => {
      const createAndgoToBranch = (branch?: string) => {
        // Set pending fork
        const currentParams = new URLSearchParams(window.location.search);
        const newUrl = new URL(
          (env.PUBLIC_BASE_PATH ?? "") +
            `/github/${editor.owner}/${editor.repo}${
              branch ? "/" + branch : ""
            }`,
          window.location.origin,
        );
        newUrl.searchParams.set("create", "true");
        currentParams.forEach((value, key) =>
          newUrl.searchParams.set(key, value),
        );
        window.location.href = newUrl.toString();
      };

      if (!gitHubScopes?.publicRepos) {
        const user = await sessionApi.signIn({
          type: "github",
          includedScopes: isPrivate ? "private_repos" : "public_repos",
        });

        if (user) {
          createAndgoToBranch();
        }

        // If user signs in it will reload the project editor as it needs to fetch the branch again
        // with correct auth
        return;
      }

      if (target === "CONTRIBUTION_BRANCH") {
        startBranchingAnimation();
        dispatchBranch({ type: "CREATE_CONTRIBUTION_BRANCH" });
        return;
      } else if (!hasBranchWriteAccess(authorization)) {
        window.location.href = paths.dashboard.recent(userWorkspace?.id, {
          import_repo: `${owner}/${repo}`,
        });
      } else if (newBranchName) {
        createAndgoToBranch(newBranchName);
        startBranchingAnimation();
      } else if (sourceBranchName === name) {
        startBranchingAnimation();
        dispatchBranch({
          type: "CREATE_DRAFT_BRANCH",
          sourceBranchName,
        });
      } else {
        // Branch from main when on a different branch
        // We need to refresh editor as you are moving to a completely different
        // VM
        window.location.href = `${router.instance.url("githubRepo", {
          owner,
          repo,
        })}?workspaceId=${workspace?.id}&create=true`;
      }
    },
    subtitle:
      target === "CONTRIBUTION_BRANCH"
        ? "Quickly contribute to open source projects"
        : "Create new branch",
  };
};
